import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authHeader } from '../../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const INITIAL_AUDIO_STATE = {
  status: null,
  message: null,
  jobId: null,
  retryAfter: null,
};

const AUDIO_STATUS_LABELS = {
  queued: '오디오 생성 대기 중입니다.',
  generating: '오디오를 생성하고 있습니다.',
  ready: '오디오가 준비되었습니다.',
  failed: '오디오 생성이 실패했습니다.',
  expired: '오디오가 만료되었습니다.',
};

const fetchReport = async (reportId) => {
  const { data } = await axios.get(`${API_BASE_URL}/api/v1/reports/${reportId}`, {
    headers: authHeader(),
  });
  return data;
};

export default function useReportDetailController({ reportId, isApp }) {
  const navigate = useNavigate();
  const {
    data: report,
    isLoading,
    isError,
    error,
  } = useQuery(['report', reportId], () => fetchReport(reportId), {
    enabled: !!reportId,
    staleTime: 5 * 60 * 1000,
  });

  const [reportErrorMessage, setReportErrorMessage] = useState('');
  const [isReportErrorModalOpen, setIsReportErrorModalOpen] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackDate, setFeedbackDate] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioState, setAudioState] = useState(INITIAL_AUDIO_STATE);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioError, setAudioError] = useState(null);

  const audioPlayerRef = useRef(null);
  const audioPollTimeoutRef = useRef(null);
  const audioSectionRef = useRef(null);
  const isMountedRef = useRef(true);

  const clearAudioPolling = useCallback(() => {
    if (audioPollTimeoutRef.current) {
      clearTimeout(audioPollTimeoutRef.current);
      audioPollTimeoutRef.current = null;
    }
  }, []);

  const getErrorMessage = useCallback((err, fallback) => {
    const rawMessage = (() => {
      if (typeof axios.isAxiosError === 'function' && axios.isAxiosError(err)) {
        return err?.response?.data?.detail || err?.response?.data?.message || err?.message || fallback;
      }
      if (err && typeof err === 'object' && 'message' in err) {
        return err.message || fallback;
      }
      return fallback;
    })();

    if (typeof rawMessage === 'string') {
      const lowered = rawMessage.toLowerCase();
      if (lowered.includes('requested language code') || lowered.includes('doesn\'t match the voice')) {
        return '오디오 생성이 지연되고 있습니다. 잠시만 더 기다려 주세요.';
      }
    }

    return rawMessage;
  }, []);

  const updateAudioState = useCallback((updater) => {
    setAudioState((prev) => {
      const partial = typeof updater === 'function' ? updater(prev) : updater;
      return { ...prev, ...partial };
    });
  }, []);

  const fetchAudioPlay = useCallback(async (token, jobId) => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/reports/${reportId}/audio/play`, {
        params: { token },
        headers: authHeader(),
      });
      if (!isMountedRef.current) return;
      setAudioUrl(data.url);
      updateAudioState({
        status: 'ready',
        jobId,
        message: '오디오가 준비되었습니다.',
        retryAfter: null,
      });
      setAudioError(null);
    } catch (err) {
      if (!isMountedRef.current) return;
      setAudioError(getErrorMessage(err, '오디오 재생 URL 발급에 실패했습니다.'));
    } finally {
      if (isMountedRef.current) {
        setAudioLoading(false);
      }
    }
  }, [getErrorMessage, reportId, updateAudioState]);

  const scheduleStatusPoll = useCallback((jobId, retryAfterSec = 5) => {
    clearAudioPolling();
    const delayMs = Math.max(retryAfterSec || 5, 2) * 1000;

    audioPollTimeoutRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/reports/${reportId}/audio/status`, {
          headers: authHeader(),
        });
        if (!isMountedRef.current) return;

        updateAudioState({
          status: data.status,
          message: data.message ?? null,
          jobId: data.job_id ?? jobId ?? null,
          retryAfter: data.retry_after ?? null,
        });

        if (data.status === 'ready') {
          clearAudioPolling();
          try {
            const prepareAgain = await axios.post(`${API_BASE_URL}/api/reports/${reportId}/audio/prepare`, null, {
              headers: authHeader(),
            });
            if (!isMountedRef.current) return;
            const prepareData = prepareAgain.data || {};
            updateAudioState((prev) => ({
              status: prepareData.status || prev.status || 'ready',
              message: prepareData.message ?? prev.message,
              jobId: prepareData.job_id ?? prev.jobId ?? data.job_id ?? jobId ?? null,
              retryAfter: prepareData.retry_after ?? null,
            }));

            if (prepareData.play_token) {
              await fetchAudioPlay(prepareData.play_token, prepareData.job_id ?? data.job_id ?? jobId ?? null);
            } else {
              setAudioError('오디오 재생 토큰을 발급받지 못했습니다. 다시 시도해 주세요.');
              setAudioLoading(false);
            }
          } catch (err) {
            if (!isMountedRef.current) return;
            setAudioError(getErrorMessage(err, '오디오 재생 정보를 갱신하는 데 실패했습니다.'));
            setAudioLoading(false);
          }
          return;
        }

        if (data.status === 'failed') {
          clearAudioPolling();
          setAudioError(data.message || '오디오 생성이 실패했습니다.');
          setAudioLoading(false);
          return;
        }

        if (data.status === 'expired') {
          clearAudioPolling();
          setAudioError('오디오가 만료되었습니다. 다시 생성해 주세요.');
          setAudioLoading(false);
          return;
        }

        scheduleStatusPoll(data.job_id ?? jobId ?? null, data.retry_after ?? retryAfterSec ?? 5);
      } catch (err) {
        if (!isMountedRef.current) return;
        clearAudioPolling();
        setAudioError(getErrorMessage(err, '오디오 상태를 확인하는 데 실패했습니다.'));
        setAudioLoading(false);
      }
    }, delayMs);
  }, [clearAudioPolling, fetchAudioPlay, getErrorMessage, reportId, updateAudioState]);

  const handleAudioOverviewClick = useCallback(async () => {
    if (!reportId) {
      setAudioError('리포트 ID를 확인할 수 없습니다.');
      return;
    }

    clearAudioPolling();
    setAudioUrl(null);
    setAudioState(INITIAL_AUDIO_STATE);
    setAudioError(null);
    setAudioLoading(true);

    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/reports/${reportId}/audio/prepare`, null, {
        headers: authHeader(),
      });
      if (!isMountedRef.current) return;

      updateAudioState({
        status: data.status,
        message: data.message ?? null,
        jobId: data.job_id ?? null,
        retryAfter: data.retry_after ?? null,
      });

      if (data.status === 'ready' && data.play_token) {
        await fetchAudioPlay(data.play_token, data.job_id ?? null);
        return;
      }

      if (data.status === 'failed') {
        setAudioError(data.message || '오디오 생성이 실패했습니다.');
        setAudioLoading(false);
        return;
      }

      if (data.status === 'expired') {
        setAudioError('오디오가 만료되었습니다. 다시 생성해 주세요.');
        setAudioLoading(false);
        return;
      }

      scheduleStatusPoll(data.job_id ?? null, data.retry_after ?? 5);
    } catch (err) {
      if (!isMountedRef.current) return;
      setAudioError(getErrorMessage(err, '오디오 브리핑 준비 요청에 실패했습니다.'));
      setAudioLoading(false);
    }
  }, [clearAudioPolling, fetchAudioPlay, getErrorMessage, reportId, scheduleStatusPoll, updateAudioState]);

  const handleFeedback = useCallback(async (rating) => {
    if (feedbackSubmitting || feedbackRating) return;
    setFeedbackError(null);
    const rid = report?.report_id || reportId;
    if (!rid) {
      setFeedbackError('리포트 ID를 확인할 수 없어 피드백을 제출할 수 없습니다.');
      return;
    }

    try {
      setFeedbackSubmitting(true);
      setFeedbackRating(rating);
      setFeedbackDate(new Date().toISOString());
      const res = await fetch(`${API_BASE_URL}/api/v1/reports/${rid}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `피드백 저장 실패 (status ${res.status})`);
      }
      const data = await res.json();
      setFeedbackRating(data.feedback_rating || rating);
      setFeedbackDate(data.feedback_date || null);
    } catch (err) {
      const msg = err && err.message === 'Failed to fetch'
        ? '네트워크 응답을 읽는 중 문제가 발생했습니다. 잠시 후 다시 확인해 주세요. 서버에는 저장되었을 수 있습니다.'
        : (err?.message || '피드백 저장 중 오류가 발생했습니다.');
      setFeedbackError(msg);
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [feedbackRating, feedbackSubmitting, report?.report_id, reportId]);

  const navigateToActionPlan = useCallback(() => {
    const rid = report?.report_id || reportId;
    if (!rid) return;
    navigate(isApp ? `/app/action-plans/report/${rid}` : `/action-plans/report/${rid}`);
  }, [isApp, navigate, report?.report_id, reportId]);

  useEffect(() => {
    setAudioLoading(false);
    setAudioState(INITIAL_AUDIO_STATE);
    setAudioUrl(null);
    setAudioError(null);
    setFeedbackSubmitting(false);
    setFeedbackRating(null);
    setFeedbackDate(null);
    setFeedbackError(null);
    setReportErrorMessage('');
    setIsReportErrorModalOpen(false);
    clearAudioPolling();
  }, [reportId, clearAudioPolling]);

  useEffect(() => () => {
    isMountedRef.current = false;
    clearAudioPolling();
  }, [clearAudioPolling]);

  useEffect(() => {
    if (audioUrl && audioPlayerRef.current) {
      try {
        audioPlayerRef.current.load();
      } catch {
        // ignore media reload errors
      }
    }
  }, [audioUrl]);

  useEffect(() => {
    if (!audioLoading) return undefined;

    const timer = setTimeout(() => {
      if (audioSectionRef.current) {
        audioSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [audioLoading]);

  useEffect(() => {
    if (report) {
      setFeedbackRating(report.feedback_rating || null);
      setFeedbackDate(report.feedback_date || null);
    }
  }, [report]);

  useEffect(() => {
    if (!isError) return;

    let friendlyMessage = '리포트를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';

    if (typeof axios.isAxiosError === 'function' && axios.isAxiosError(error)) {
      const status = error?.response?.status;
      const backendDetail =
        (error?.response?.data && (error.response.data.detail || error.response.data.message)) || undefined;

      if (status === 403) {
        friendlyMessage = backendDetail || '리포트를 조회할 권한이 없습니다.';
      } else if (status === 404) {
        friendlyMessage = backendDetail || '리포트를 찾을 수 없습니다.';
      } else {
        friendlyMessage = backendDetail || error.message || friendlyMessage;
      }
    } else if (error && typeof error === 'object' && 'message' in error) {
      friendlyMessage = error.message || friendlyMessage;
    }

    setReportErrorMessage(friendlyMessage);
    setIsReportErrorModalOpen(true);
  }, [error, isError]);

  const serverRatingRaw = typeof report?.feedback_rating === 'string' ? report.feedback_rating : null;
  const serverRatingTrimmed = serverRatingRaw ? serverRatingRaw.trim().toLowerCase() : null;
  const serverRating = serverRatingTrimmed === 'helpful' || serverRatingTrimmed === 'needs_improvement'
    ? serverRatingTrimmed
    : null;
  const effectiveRating = (feedbackRating ?? serverRating) || null;
  const hasFeedback = effectiveRating === 'helpful' || effectiveRating === 'needs_improvement';
  const effectiveDate = (feedbackDate ?? report?.feedback_date) || null;
  const audioStatusLabel = audioState.status ? (AUDIO_STATUS_LABELS[audioState.status] || audioState.status) : null;
  const createdAtLabel = report?.created_at ? new Date(report.created_at).toLocaleString() : '';

  return {
    report,
    isLoading,
    reportErrorMessage,
    isReportErrorModalOpen,
    setIsReportErrorModalOpen,
    feedbackSubmitting,
    handleFeedback,
    feedbackError,
    hasFeedback,
    effectiveRating,
    effectiveDate,
    audioLoading,
    handleAudioOverviewClick,
    audioState,
    audioStatusLabel,
    audioError,
    audioUrl,
    audioPlayerRef,
    audioSectionRef,
    createdAtLabel,
    navigateToActionPlan,
  };
}
